<script>
import ToggleButton from "@/components/global/eg_toggle_button.vue";
import EgFormGroup from "@/components/global/eg_form_group.vue";
import EssayLanguageSelect from "@/components/dashboard/components/essay_language_select.vue";
import RubricGradeLevelRadioGroup from "@/components/global/form/rubric_grade_level_radio_group.vue";

export const settingProps = {
  grade: Boolean,
  subject: Boolean,
  category: Boolean,
  intensity: Boolean,
  language: Boolean,
};

export default {
  name: "NewRubricSettings",
  components: {
    EgFormGroup,
    ToggleButton,
    EssayLanguageSelect,
    RubricGradeLevelRadioGroup,
  },
  inject: ["newRubricSettings", "updateRubricSettings", "newRubricData"],
  props: {
    ...settingProps,
    title: {
      required: false,
      type: String,
      default: "",
    },
    autoCommit: { type: Boolean, default: true },
  },
  emits: ["onCancel", "onSave"],
  data() {
    return {
      gradeLevel: this.newRubricSettings.selectedGradeLevel,
      categoryIdx: this.newRubricSettings.selectedCategoryIdx,
      intensityIdx: this.newRubricSettings.selectedIntensityIdx,
      languageId: this.newRubricSettings.selectedLanguageId,
    };
  },
  methods: {
    /**
     * @param  property {"grade"|"category"|"intensity"|"language"}
     * @param value {number}
     */
    updateValue(property, value) {
      switch (property) {
        case "language":
          this.languageId = value;
          break;
        case "intensity":
          this.intensityIdx = value;
          break;
        case "gradeLevel":
          this.gradeLevel = value;
          break;
        case "category":
          this.categoryIdx = value;
          break;
      }
      if (this.autoCommit) {
        this.updateRubricSettings("selectedGradeLevel", this.gradeLevel);
        this.updateRubricSettings("selectedCategoryIdx", this.categoryIdx);
        this.updateRubricSettings("selectedIntensityIdx", this.intensityIdx);
        this.updateRubricSettings("selectedLanguageId", this.languageId);
      }
    },
  },
};
</script>

<template>
  <div class="settings-wrapper">
    <div v-if="title" class="settings-header">
      <h2 class="settings-title heading-s">{{ title }}</h2>
    </div>
    <eg-form-group
      v-if="grade"
      :is-custom="false"
      :label="$t('rubrics.studentLevel')"
      :description="$t('rubrics.studentLevelDescription')"
    >
      <rubric-grade-level-radio-group
        :value="gradeLevel"
        horizontal
        :required="true"
        @input="updateValue('gradeLevel', $event)"
      />
    </eg-form-group>

    <eg-form-group
      v-if="category"
      :is-custom="false"
      :label="$t('rubrics.categoryLabel')"
      :description="$t('rubrics.categoryDescription')"
    >
      <div class="flex-gap d-flex flex-wrap">
        <ToggleButton
          v-for="(category, idx) in newRubricData.categories"
          :key="idx"
          horizontal
          :label="
            category.translationKey
              ? $t(category.translationKey)
              : category.label
          "
          :pressed="categoryIdx === idx"
          @click="updateValue('category', idx)"
        />
      </div>
    </eg-form-group>

    <eg-form-group
      v-if="intensity"
      :is-custom="false"
      :label="$t('rubrics.gradingIntensityLabel')"
      :description="$t('rubrics.gradingIntensityDesc')"
    >
      <div class="flex-gap-1 d-flex flex-wrap">
        <ToggleButton
          v-for="(intensity, idx) in newRubricData.intensities"
          :key="idx"
          class="intensity-button"
          :label="
            intensity.translationKey
              ? $t(intensity.translationKey)
              : intensity.label
          "
          :pressed="intensityIdx === idx"
          horizontal
          @click="updateValue('intensity', idx)"
        >
        </ToggleButton>
      </div>
    </eg-form-group>

    <eg-form-group
      v-if="language"
      :is-custom="false"
      :label="$t('rubrics.language')"
      :description="$t('rubrics.languageDescription')"
    >
      <div class="flex-gap d-flex flex-wrap">
        <essay-language-select
          :value="languageId"
          @input="updateValue('language', $event)"
        />
      </div>
    </eg-form-group>

    <div v-if="!autoCommit" class="d-flex justify-content-end mx-2">
      <eg-b-button variant="outline-info" @click="$emit('onCancel')">
        {{ $t("common.cancel") }}
      </eg-b-button>
      <eg-b-button
        class="ml-3"
        variant="info"
        @click="
          $emit('onSave', {
            gradeLevel,
            categoryIdx,
            intensityIdx,
            languageId,
          })
        "
      >
        {{ $t("rubrics.saveChanges") }}
      </eg-b-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.settings-wrapper {
  ::v-deep {
    .form-group {
      margin-bottom: 1rem;
    }
    .eg-form-group {
      margin-bottom: 1.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .eg-form-group__label {
      font-size: 1rem;
      font-weight: 600;
      color: var(--color-text-neutral-on-white, #1f2937);
      margin-bottom: 0.5rem;
    }

    .eg-form-group__description {
      font-size: 0.875rem;
      color: var(--color-text-neutral-subtle, #6b7280);
      margin-bottom: 0.75rem;
    }
    .form-group .custom-form-group {
      margin-bottom: 0;
    }
  }
}
.settings-header {
  margin-bottom: 1.5rem;

  .settings-title {
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.5;
    color: var(--color-text-neutral-on-white, #1f2937);
    margin: 0;
  }
}
.intensity-button {
  min-width: 100px;
  font-size: 14px;
}
</style>
